import React, { useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

interface Crop {
  id: number;
  name: string;
  name_hindi: string;
  icon: string;
  created_at: string;
}

const API_URL = 'https://mandilive.in/api/crops'; // Adjust if different

function getImageUrl(path: string) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  // Normalize backslashes and slashes for URLs
  return `https://mandilive.in${path.replaceAll('\\', '/').replaceAll('\\', '/')}`;
}

const CropsSection: React.FC = () => {
  const [crops, setCrops] = useState<Crop[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addForm, setAddForm] = useState({ name: '', name_hindi: '', icon: null as File | null });
  const [adding, setAdding] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleting, setDeleting] = useState<number | null>(null);
  useEffect(() => {
    fetchCrops();
  }, []);

  const fetchCrops = async () => {
    setLoading(true);
    setError('');
    try {
      // Add cache-busting parameter
      const cacheBuster = new Date().getTime();
      console.log('Fetching crops from:', `${API_URL}?_cb=${cacheBuster}`);
      
      // Using only URL-based cache-busting to prevent stale data
      // No custom headers to avoid CORS preflight issues
      const res = await fetch(`${API_URL}?_cb=${cacheBuster}`, {
        method: 'GET'
      });
      
      console.log('Fetch crops response status:', res.status);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      
      console.log('Crops API response:', data);
      console.log('Response data type:', typeof data);
      console.log('Response data.data type:', typeof data.data);
      console.log('Is data.data an array?', Array.isArray(data.data));
      console.log('Response data.data:', data.data);
      
      if (data && data.data && Array.isArray(data.data)) {
        console.log(`Setting ${data.data.length} crops to state`);
        console.log('Crops to set:', data.data);
        setCrops(data.data);
      } else {
        console.log('No crops data found in response or invalid format');
        console.log('Full response structure:', JSON.stringify(data, null, 2));
        setCrops([]);
      }
    } catch (err: any) {
      // Enhanced error logging
      console.error('Crops fetch error:', err);
      const errorMessage = err?.message || String(err);
      console.error('Error details:', errorMessage);
      
      // More user-friendly error message with network error detection
      if (errorMessage.includes('Failed to fetch')) {
        setError('Network error: Could not connect to the server. Please check your internet connection.');
      } else if (errorMessage.includes('NetworkError')) {
        setError('CORS error: The server refused the connection. Please contact the administrator.');
      } else {
        setError('Failed to fetch crops: ' + errorMessage);
      }
      setCrops([]);
    }
    setLoading(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    if (name === 'icon' && files && files[0]) {
      setAddForm(f => ({ ...f, icon: files[0] }));
    } else {
      setAddForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleAddCrop = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('name', addForm.name);
      formData.append('name_hindi', addForm.name_hindi);
      if (addForm.icon) formData.append('icon', addForm.icon);

      const res = await fetch(API_URL, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData?.error || 'Failed to add crop');
      }
      // Success
      setAddForm({ name: '', name_hindi: '', icon: null });
      setSuccessMessage('Crop added successfully!');
      setShowSuccess(true);
      fetchCrops();
      setTimeout(() => setShowSuccess(false), 2500);
    } catch (err: any) {
      setError('Failed to add crop: ' + (err?.message || err));
    }
    setAdding(false);
  };

  const handleDeleteCrop = async (cropId: number, cropName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${cropName}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(cropId);
    setError('');
    try {
      console.log('Deleting crop with ID:', cropId); // Debug log
      const res = await fetch(`${API_URL}/${cropId}`, {
        method: 'DELETE',
      });
      
      console.log('Delete response status:', res.status); // Debug log
      
      if (!res.ok) {
        const errData = await res.json();
        console.error('Delete error response:', errData); // Debug log
        throw new Error(errData?.message || 'Failed to delete crop');
      }
      
      const responseData = await res.json();
      console.log('Delete success response:', responseData); // Debug log
      
      // Success - refresh the crops list from server to ensure UI is in sync
      await fetchCrops();
      setSuccessMessage(`"${cropName}" deleted successfully!`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
    } catch (err: any) {
      console.error('Delete crop error:', err); // Debug log
      setError('Failed to delete crop: ' + (err?.message || err));
    }
    setDeleting(null);
  };

  return (
    <div className="space-y-8">
      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
          <div className="bg-white p-6 rounded shadow-lg text-center">
            <div className="text-green-600 text-2xl mb-2">✔️</div>
            <div className="font-semibold mb-2">{successMessage}</div>
            <button
              className="mt-2 px-4 py-1 bg-green-600 text-white rounded hover:bg-green-700"
              onClick={() => setShowSuccess(false)}
            >OK</button>
          </div>
        </div>
      )}
      <div>
        <h1 className="text-2xl font-bold mb-2">Crops Management</h1>
        <p className="text-gray-600 mb-4">Manage crops, add new crops, and view all crops in the system.</p>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold mb-2">Add New Crop</h2>
        <form className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end" onSubmit={handleAddCrop}>
          <div>
            <label className="block text-sm font-medium mb-1">Name (English)</label>
            <input name="name" value={addForm.name} onChange={handleInputChange} className="w-full border rounded px-2 py-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name (Hindi)</label>
            <input name="name_hindi" value={addForm.name_hindi} onChange={handleInputChange} className="w-full border rounded px-2 py-1" required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Icon/Image</label>
            <input name="icon" type="file" accept="image/*" onChange={handleInputChange} className="w-full" required />
          </div>
          <button type="submit" className="md:col-span-3 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60" disabled={adding}>
            <FiPlus className="mr-2" /> Add Crop
          </button>
        </form>
        {error && <div className="text-red-600 mt-2">{error}</div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">All Crops</h2>
          <button
            onClick={fetchCrops}
            disabled={loading}
            className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            title="Refresh crops list"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            ) : (
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </button>
        </div>
        {loading ? <div>Loading...</div> : crops.length === 0 ? <div>No crops found.</div> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {crops.map(crop => (
              <div key={crop.id} className="flex items-center justify-between border p-3 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <img src={getImageUrl(crop.icon)} alt={crop.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-semibold">{crop.name} <span className="text-gray-500">({crop.name_hindi})</span></div>
                    <div className="text-xs text-gray-400">Added: {new Date(crop.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteCrop(crop.id, crop.name)}
                  disabled={deleting === crop.id}
                  className="flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={`Delete ${crop.name}`}
                >
                  {deleting === crop.id ? (
                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <FiTrash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CropsSection;

